/**
 * The finding page's decision control, at the right of its top bar.
 *
 * - Open, with a current proposed fix: Apply fix (a short confirm), and a
 *   chevron listing the other ways to resolve.
 * - Open, without one: Resolve, a compact form in a popover: the requirement,
 *   one choice, a note, and a confirm that names what it records.
 * - Decided: the decision as a pill, and Reopen.
 * - Someone who cannot decide sees the pill on a decided finding and nothing
 *   on an open one; no control is ever shown disabled for lack of permission.
 *
 * Dismiss lives in the page's More menu with the other secondary actions.
 * Which of these shows is `headerDecision` (resolveControl).
 *
 * @module components/regulation/finding/FindingDecisionControl
 */
import { ChevronDownIcon, CircleCheckIcon, FileCheckIcon, FileUpIcon, HistoryIcon, LoaderCircleIcon, RotateCcwIcon } from "lucide-react";
import { useRef, useState } from "react";

import { Button } from "../../ui/button";
import { Menu, MenuGroup, MenuGroupLabel, MenuItem, MenuPopup, MenuTrigger } from "../../ui/menu";
import { Popover, PopoverPopup, PopoverTrigger } from "../../ui/popover";
import { assessmentBaselineKnown } from "../policy/resolutionCandidates";
import { ResolveFindingForm, type ResolutionEvidence, type ResolveSubject } from "../policy/ResolveRecommendationDialog";
import { StatusIcon } from "./findingGlyphs";
import type { FindingItem } from "./findingItem";
import { decisionPillLabel, headerDecision, resolveChoiceLabel, resolveChoices, resolveTargetOf, type ResolveChoice } from "./resolveControl";

export interface ApplyFixControl {
  /** A current proposed fix may be applied. */
  readonly available: boolean;
  readonly changes: number;
  /** The version the fix was already saved as, when only its decision failed. */
  readonly savedVersion: number | null;
  readonly applying: boolean;
  /** Saves the fix as the next version and resolves; true when it worked. */
  readonly onApply: () => Promise<boolean>;
}

/** The finding's subject for the resolve form; null when it has nothing to resolve against. */
export function resolveSubjectOf(item: FindingItem): ResolveSubject | null {
  const target = resolveTargetOf(item);
  if (target === null) return null;
  const finding = item.finding;
  return {
    target,
    id: item.id,
    policyId: item.policyId,
    raisedAt: finding?.detectedAt ?? item.suggestion?.createdAt ?? new Date(item.raisedAt).toISOString(),
    documentName: item.documentName,
    requirement: item.requiredChanges.length > 0 ? item.requiredChanges : item.problem ? [item.problem] : [],
    ...(finding ? { assessed: { assessedVersion: finding.assessedVersion ?? null, assessedRevision: finding.assessedRevision ?? null } } : {}),
  };
}

function DecisionPill({ item }: { readonly item: FindingItem }) {
  if (item.status === "open") return null;
  return (
    <span
      className="inline-flex h-7 max-w-56 items-center gap-1.5 rounded-full border border-border/70 bg-card ps-2 pe-2.5 text-xs font-medium text-muted-foreground"
      data-header-status={item.status}
    >
      <StatusIcon status={item.status} />
      <span className="truncate">{decisionPillLabel(item.status, item.decision)}</span>
    </span>
  );
}

const CHOICE_ICON: Partial<Record<ResolveChoice, typeof HistoryIcon>> = {
  newer: HistoryIcon,
  covered: FileCheckIcon,
  upload: FileUpIcon,
};

export function FindingDecisionControl({
  organizationId,
  item,
  canManage,
  currentVersion,
  apply,
  resolving,
  onResolvingChange,
  onResolve,
  onReopen,
}: {
  readonly organizationId: string;
  readonly item: FindingItem;
  readonly canManage: boolean;
  readonly currentVersion: number | null;
  readonly apply: ApplyFixControl;
  /** Controlled, so the status picker's Resolve… opens the same form. */
  readonly resolving: boolean;
  readonly onResolvingChange: (open: boolean) => void;
  readonly onResolve: (evidence: ResolutionEvidence) => Promise<string | null>;
  readonly onReopen: () => void;
}) {
  const anchor = useRef<HTMLDivElement>(null);
  const [confirmingApply, setConfirmingApply] = useState(false);
  const [initialChoice, setInitialChoice] = useState<ResolveChoice | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const subject = resolveSubjectOf(item);
  const lead = headerDecision({ status: item.status, canManage, canApply: apply.available });

  if (lead === "none") return null;
  if (lead === "status" || lead === "reopen") {
    return (
      <>
        <DecisionPill item={item} />
        {lead === "reopen" ? (
          <Button size="sm" variant="outline" onClick={onReopen} data-header-reopen="">
            <RotateCcwIcon aria-hidden />
            Reopen
          </Button>
        ) : null}
      </>
    );
  }

  const setResolving = (open: boolean, choice?: ResolveChoice) => {
    if (busy) return;
    // A menu item opens the form on its choice; the status picker and Resolve on the default.
    setInitialChoice(open ? choice : undefined);
    onResolvingChange(open);
  };
  const others = subject ? resolveChoices(subject.target, { stamped: subject.target === "finding" && assessmentBaselineKnown(subject.assessed) }) : [];
  const resolveButton = (
    <PopoverTrigger render={<Button size="sm" data-header-resolve="" />}>
      <CircleCheckIcon aria-hidden />
      Resolve
    </PopoverTrigger>
  );

  return (
    <div ref={anchor} className="flex items-center">
      {lead === "apply" ? (
        <>
          <Popover open={confirmingApply} onOpenChange={(next) => !apply.applying && setConfirmingApply(next)}>
            <PopoverTrigger disabled={apply.applying} render={<Button size="sm" className="rounded-e-none before:rounded-e-none" data-header-apply="" />}>
              {apply.applying ? <LoaderCircleIcon aria-hidden className="animate-spin" /> : <CircleCheckIcon aria-hidden />}
              {apply.applying ? "Applying…" : "Apply fix"}
            </PopoverTrigger>
            <PopoverPopup align="end" className="w-72" data-apply-confirm="">
              <p className="text-[13px] font-medium">Apply the proposed fix?</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {apply.savedVersion !== null ? (
                  <>
                    Inspector&rsquo;s proposed PDF is already saved as File v{apply.savedVersion} of {item.documentName}. This{" "}
                    {item.finding ? "finding" : "suggestion"} is resolved with it as evidence.
                  </>
                ) : (
                  <>
                    Inspector&rsquo;s proposed PDF ({apply.changes} {apply.changes === 1 ? "change" : "changes"}) becomes File v{(currentVersion ?? 0) + 1} of{" "}
                    {item.documentName}. This {item.finding ? "finding" : "suggestion"} is resolved with it as evidence.
                  </>
                )}
              </p>
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" disabled={apply.applying} onClick={() => setConfirmingApply(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={apply.applying}
                  onClick={() => void apply.onApply().then((done) => done && setConfirmingApply(false))}
                  data-apply-confirm-submit=""
                >
                  {apply.applying ? <LoaderCircleIcon aria-hidden className="animate-spin" /> : null}
                  {apply.applying ? "Applying…" : apply.savedVersion !== null ? `Resolve with File v${apply.savedVersion}` : `Apply as File v${(currentVersion ?? 0) + 1}`}
                </Button>
              </div>
            </PopoverPopup>
          </Popover>
          <Menu>
            <MenuTrigger
              disabled={apply.applying}
              render={<Button size="sm" className="-ms-px rounded-s-none border-s-primary-foreground/24 px-1.5 before:rounded-s-none" aria-label="Resolve another way" data-header-resolve-menu="" />}
            >
              <ChevronDownIcon aria-hidden className="opacity-70" />
            </MenuTrigger>
            <MenuPopup align="end" className="w-64">
              <MenuGroup>
                <MenuGroupLabel>Resolve without the fix</MenuGroupLabel>
                {others.map((choice) => {
                  const Icon = CHOICE_ICON[choice] ?? CircleCheckIcon;
                  return (
                    <MenuItem key={choice} onClick={() => setResolving(true, choice)} data-resolve-another={choice}>
                      <Icon />
                      {resolveChoiceLabel(choice, currentVersion)}
                    </MenuItem>
                  );
                })}
              </MenuGroup>
            </MenuPopup>
          </Menu>
        </>
      ) : null}
      {subject ? (
        <Popover open={resolving} onOpenChange={(next) => setResolving(next)}>
          {lead === "resolve" ? resolveButton : null}
          <PopoverPopup anchor={anchor} align="end" className="w-[26rem]" data-resolve-popover="">
            <ResolveFindingForm
              key={`${item.id}:${initialChoice ?? ""}`}
              organizationId={organizationId}
              subject={subject}
              initialChoice={initialChoice}
              onResolve={onResolve}
              onBusyChange={setBusy}
              onClose={() => {
                setBusy(false);
                setInitialChoice(undefined);
                onResolvingChange(false);
              }}
            />
          </PopoverPopup>
        </Popover>
      ) : null}
    </div>
  );
}
